import React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

// Stage picker for "Креирај растур" (6423) — Размерување shows ALL subprocesses' rows
// combined in one table (activeStage is pinned to the "__razmer__" sentinel there), so
// there is no single "active stage" to derive a subprocessId from. This modal lets the
// operator pick WHICH subprocess's confirmed rastur rows to issue as a 6423 document.
// Reuses the same overlay/header/scrollable-list shell as RNRazmerDispatchModal — a new
// component because the content (a one-click stage list) doesn't fit that modal's
// row-table + recipient-select + send-button shape without bending it from the outside.

export interface RNRasturStageOption {
  subprocessId: string;
  name: string;
  rowCount: number;
  existingDocumentNumber?: string;
}

interface Props {
  isOpen: boolean;
  title?: string;
  stages: RNRasturStageOption[];
  isSubmitting?: boolean;
  onSelect: (subprocessId: string) => void;
  onClose: () => void;
}

const RNRasturStageModal: React.FC<Props> = ({ isOpen, title, stages, isSubmitting = false, onSelect, onClose }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-gray-800">{title || t("os.razmer.rastur6423ModalTitle")}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50"
            aria-label={t("rnDispatchModal.close")}
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-auto px-5 py-4">
          {stages.length === 0 ? (
            <div className="py-6 text-center text-xs text-gray-500">{t("os.razmer.rastur6423ModalEmpty")}</div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {stages.map((stage) => {
                const disabled = isSubmitting || !!stage.existingDocumentNumber;
                return (
                  <li key={stage.subprocessId}>
                    <button
                      type="button"
                      onClick={() => onSelect(stage.subprocessId)}
                      disabled={disabled}
                      title={
                        stage.existingDocumentNumber
                          ? t("os.razmer.rastur6423AlreadyIssued", { number: stage.existingDocumentNumber })
                          : undefined
                      }
                      className={`flex w-full items-center justify-between gap-3 rounded-md px-2 py-2.5 text-left text-xs transition-colors focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${
                        disabled ? "cursor-not-allowed text-gray-400" : "text-gray-800 hover:bg-blue-50"
                      }`}
                    >
                      <span className="font-medium">{stage.name}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          disabled ? "bg-gray-100 text-gray-400" : "bg-blue-50 text-blue-700"
                        }`}
                      >
                        {t("os.razmer.rastur6423ModalRowCount", { count: stage.rowCount })}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default RNRasturStageModal;
